import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { Bilgi } from '../satis-analiz/Bilgi';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import { oranMetni } from '../satis-analiz/bicim';
import type { Donem } from '../satis-analiz/donem';
import type { StokKaynaklari } from './ekranKaynaklari';
import { useStokSorgu, type StokKaynak, type StokSorguFiltresi } from './useStokVerisi';
import { OLU_TURLERI, oluKovaOzeti, type OluStokSatiri, type OluTur } from './toplama';
import { kapsamNotuGorunsunMu } from './kapsam';
import { kategoriSuz } from './kategori';
import { StokDagilimBlok, UrunSermayeBlok } from './SermayeDetayBloklari';
import { KartHatasi } from './KartHatasi';
import type { StokDetay } from './detay/stokDetayTipleri';

interface Props {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  sube?: string | null;
  /** Sayfanin kategori secimi; 990077 @KATEGORI bildirmez, ISTEMCIDE suzulur. ABC kategoriye ayrilamaz. */
  kategori?: string | null;
  ac?: (d: StokDetay) => void;
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 1: Ölü stok (990077), ANLIK, parametresiz. İki tür × dört gün kovası; kova tıklanınca ürünler.
// ════════════════════════════════════════════════════════════════════════════════════════════

const OLU_STOK_BILGI =
  'Bugün stoğu olan ve en az 30 gündür satmayan ürünler. Hiç satılmamış üründe gün, ilk girişten sayılır; '
  + 'yeni gelen ürün listeye girmez. Değer: eldeki miktar × son alış fiyatı.';

function OluTurListesi({
  tur, baslik, satirlar, ac,
}: {
  tur: OluTur;
  baslik: string;
  satirlar: OluStokSatiri[];
  ac?: (d: StokDetay) => void;
}) {
  const kovalar = oluKovaOzeti(satirlar, tur);
  const urun = kovalar.reduce((t, k) => t + k.urun, 0);
  const deger = kovalar.reduce((t, k) => t + k.deger, 0);
  return (
    <div data-testid={`sermaye-olu-${tur}`} className="flex-1 min-w-0">
      <h3 className="text-[12px] font-medium text-ink-500">{baslik}</h3>
      <p className="mt-0.5 mb-2 text-[13px] tnum text-ink-900">
        <span data-testid={`sermaye-olu-${tur}-toplam`} className="font-semibold">{fmtTRY(deger)}</span>
        <span className="text-ink-500"> · {fmtNum(urun)} ürün</span>
      </p>
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {kovalar.map((k) => {
          const icerik = (
            <>
              <span className="text-ink-900 truncate min-w-0">{k.kova}</span>
              <span className="flex items-center gap-3 shrink-0 tnum">
                <span className="text-ink-500">{fmtNum(k.urun)} ürün</span>
                <span className="text-ink-900 font-medium">{fmtTRY(k.deger)}</span>
              </span>
            </>
          );
          const testid = `sermaye-olu-${tur}-${k.kova}`;
          return (
            <li key={k.kova}>
              {ac && k.urun > 0 ? (
                <button
                  type="button"
                  data-testid={testid}
                  onClick={() => ac({ tur: 'oluStok', grup: tur, kova: k.kova })}
                  className="focus-ring w-full flex items-center justify-between gap-3 text-[13px] hover:underline underline-offset-2 text-left"
                >
                  {icerik}
                </button>
              ) : (
                <div data-testid={testid} className="flex items-center justify-between gap-3 text-[13px]">
                  {icerik}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function OluStokKarti({
  kaynak, f, kategori, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  kategori: string | null;
  ac?: (d: StokDetay) => void;
}) {
  const q = useStokSorgu<OluStokSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="sermaye-olu-stok-iskelet" baslik="Ölü stok" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return <KartHatasi testid="sermaye-olu-stok-hata" baslik="Ölü stok" yukseklik={200} mesaj="Ölü stok yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = kategoriSuz(q.data.data, kategori);

  return (
    <div data-testid="sermaye-olu-stok" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Ölü stok"
        subtitle="Eldeki stoğu olup en az 30 gündür satmayan ürünler (anlık, bugün)"
        actions={<Bilgi testid="sermaye-olu-stok-bilgi" metin={OLU_STOK_BILGI} />}
      >
        <div className="flex flex-col sm:flex-row gap-6">
          {OLU_TURLERI.map((t) => (
            <OluTurListesi key={t.tur} tur={t.tur} baslik={t.baslik} satirlar={satirlar} ac={ac} />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 2: ABC Analizi (990045), A/B/C + D (net ciro ≤ 0)
// S352: satır tıklanınca sınıfın ürün grupları, gruptan ürünler modalda açılır (990076).
// ════════════════════════════════════════════════════════════════════════════════════════════

interface AbcSatiri { sinif: string; urun_sayisi: number; ciro: number; urun_pay: number; ciro_pay: number }

const ABC_D_BILGI = 'Dönem net cirosu sıfır ya da negatif (iade ağırlıklı) ürünler — standart ABC sözlüğünde yoktur.';

/**
 * I-5 (final-fix F-3) — `135-gizova-turetilmis-olculer.sql:9-20` şerhi: bu ciro FİŞ değil SATIR
 * tanesinde (`d.dahil_tutar`, KDV DAHİL), üstüne yalnız `stok_cinsi = 1` (ürün kartları — ambalaj/
 * depozito gibi ürün-dışı kalemler dışlanır). Kart taban/tane bilgisini kendi göstermezse kullanıcı
 * bu toplamdan fire/ciro oranı türetmeye kalkarsa (fire farklı bir tabanda) şeritteki oranı tutturamaz.
 */
const ABC_CIRO_BILGI = 'Ciro KDV dahil, satış SATIRI (dahil_tutar) tabanında; yalnız stok_cinsi=1 (ürün kartları, ambalaj/depozito hariç).';

function AbcKarti({
  kaynak, f, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  /** Yalnız ürün listesi kaynağı (abcDetay) tanımlıysa verilir; yoksa satırlar tıklanamaz çizilir. */
  ac?: (d: StokDetay) => void;
}) {
  const q = useStokSorgu<AbcSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="sermaye-abc-iskelet" baslik="ABC" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return <KartHatasi testid="sermaye-abc-hata" baslik="ABC Analizi" yukseklik={200} mesaj="ABC analizi yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data;
  if (satirlar.length === 0) return null;

  return (
    <div data-testid="sermaye-abc" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="ABC Analizi"
        subtitle="Ciroya göre kümülatif pay — A %80 / B %95 / C kalan"
        actions={<Bilgi testid="sermaye-abc-ciro-bilgi" metin={ABC_CIRO_BILGI} />}
      >
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="text-[12px] text-ink-500 border-b border-border">
              <th className="text-left font-medium py-2 pr-3">Sınıf</th>
              <th className="text-right font-medium py-2 px-3">Ürün</th>
              <th className="text-right font-medium py-2 px-3">Ciro</th>
              <th className="text-right font-medium py-2 pl-3">Ciro payı</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s) => (
              <tr
                key={s.sinif}
                data-testid={`sermaye-abc-satir-${s.sinif}`}
                onClick={ac ? () => ac({ tur: 'abc', sinif: s.sinif }) : undefined}
                className={`border-b border-border last:border-b-0${ac ? ' cursor-pointer group' : ''}`}
              >
                <td className="py-2 pr-3 text-ink-900 font-medium">
                  {ac ? (
                    // Klavye için gerçek düğme; tıklama satıra kabarır, ayrıca onClick taşımaz.
                    <button
                      type="button"
                      data-testid={`sermaye-abc-ac-${s.sinif}`}
                      aria-label={`${s.sinif} sınıfındaki ürünleri göster`}
                      className="focus-ring font-medium group-hover:underline underline-offset-2"
                    >
                      {s.sinif}
                    </button>
                  ) : s.sinif}
                  {s.sinif === 'D' && (
                    <span className="ml-1.5 inline-block align-middle" onClick={(e) => e.stopPropagation()}>
                      <Bilgi testid="sermaye-abc-d-bilgi" metin={ABC_D_BILGI} />
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">
                  {fmtNum(s.urun_sayisi)}{' '}
                  <span className="text-ink-400">({oranMetni(s.urun_pay, 1)})</span>
                </td>
                <td className="py-2 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(s.ciro)}</td>
                <td className="py-2 pl-3 text-right tnum text-ink-900 font-medium whitespace-nowrap">
                  {oranMetni(s.ciro_pay, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Blok: SIKIŞAN SERMAYE
// ════════════════════════════════════════════════════════════════════════════════════════════

const KAPSAM_NOT_METNI = 'Şube süzgeci bu blokta geçerli değil — ölçüler tüm şubeleri kapsar.';

/**
 * S352: blok iki karta indi (Ölü stok + ABC). Stok yaşlandırma ve GMROI kartları kaldırıldı
 * (yaşlandırma ERP'nin FIFO eşleşme kaydına bağlıydı ve bayat kalıyordu; GMROI geçmiş satışı bugünkü
 * alış fiyatıyla maliyetlendiriyordu). Kaynakları ekran sözlüğünde durur, burada okunmaz.
 *
 * Kapsam notu bloğun iki ds'inin (990077 / 990045) GERÇEK `@LOKASYON` bildirimine bakar (`kapsam.ts`);
 * bugün ikisi de bildirmiyor.
 */
export function SermayeBlok({ kaynaklar, donem, sube = null, kategori = null, ac }: Props) {
  const f: StokSorguFiltresi = { donem, sube, kategori: null };

  if (!kaynaklar.oluStokTur && !kaynaklar.abc) return null;

  const kapsamNotuGorunur = kapsamNotuGorunsunMu([kaynaklar.oluStokTur, kaynaklar.abc], '@LOKASYON', sube);

  return (
    <section data-testid="sermaye-blok" className="flex flex-col gap-3">
      {kapsamNotuGorunur && (
        <p data-testid="sermaye-kapsam-not" className="text-[11px] text-ink-400">
          {KAPSAM_NOT_METNI}
        </p>
      )}

      <StokDagilimBlok kaynaklar={kaynaklar} f={f} kategori={kategori} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
        <OluStokKarti kaynak={kaynaklar.oluStokTur} f={f} kategori={kategori} ac={ac} />
        <AbcKarti kaynak={kaynaklar.abc} f={f} ac={kaynaklar.abcDetay ? ac : undefined} />
      </div>
      {kategori !== null && kaynaklar.abc && (
        <p data-testid="sermaye-abc-kategori-not" className="text-[11px] text-ink-400">
          ABC Analizi kategori seçiminden etkilenmez; tüm ürünleri kapsar.
        </p>
      )}

      <UrunSermayeBlok kaynaklar={kaynaklar} f={f} kategori={kategori} />
    </section>
  );
}
